import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ALPHABET_DATA, LetterItem } from '../../core/curriculum/curriculumData';
import {
  UPPERCASE_LETTER_STROKES,
  LOWERCASE_LETTER_STROKES,
  LetterStroke,
} from '../../core/writing/letterStrokesData';
import { PixiLetterTracingCanvas } from './PixiLetterTracingCanvas';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';
import confetti from 'canvas-confetti';
import { AlphabetIcon, BackIcon, SpeakerIcon, WritingIcon } from '../../components/icons/ReadingIcons';

interface WritingViewProps {
  onBack: () => void;
}

export const WritingView: React.FC<WritingViewProps> = ({ onBack }) => {
  // Step 1: Mode choice - Uppercase (Besar) or Lowercase (Kecil)
  const [isUppercase, setIsUppercase] = useState<boolean>(true);

  // Selected Letter from ALPHABET_DATA (default to 'A')
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const selectedItem: LetterItem = ALPHABET_DATA[selectedIndex] || ALPHABET_DATA[0];

  const currentDisplayLetter = isUppercase ? selectedItem.letter : selectedItem.lowercase;

  // Active letter strokes
  const strokeMap = isUppercase ? UPPERCASE_LETTER_STROKES : LOWERCASE_LETTER_STROKES;
  const currentStrokes: LetterStroke[] = strokeMap[currentDisplayLetter] || strokeMap['A'] || [];

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [mascotMessage, setMascotMessage] = useState<string>('Pilih huruf dan ikuti nomor urutan garisnya!');
  const [mascotState, setMascotState] = useState<'idle' | 'happy' | 'encourage' | 'celebrate' | 'pointing'>('pointing');
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  const [completedLetters, setCompletedLetters] = useState<string[]>([]);
  const completionLogCountRef = useRef(0);

  // Update instructions when letter or case changes
  useEffect(() => {
    setCurrentStepIndex(0);
    setShowCelebration(false);
    const stroke1 = currentStrokes[0];
    const instructionText = stroke1
      ? `Ayo tulis huruf ${currentDisplayLetter}! Mulai dari nomor 1: ${stroke1.instruction}.`
      : `Ayo tulis huruf ${currentDisplayLetter}!`;

    setMascotMessage(instructionText);
    setMascotState('pointing');
    speechProvider.speak(instructionText, { rate: 0.88 });
  }, [selectedIndex, isUppercase]);

  const handleSelectCase = (uppercase: boolean) => {
    speechProvider.playChime('tap');
    setIsUppercase(uppercase);
    const caseText = uppercase ? 'Huruf Besar' : 'huruf kecil';
    speechProvider.speak(`Mode ${caseText}. Pilih huruf yang ingin kamu tulis!`, { rate: 0.88 });
  };

  const handleSelectLetter = (index: number) => {
    speechProvider.playChime('tap');
    setSelectedIndex(index);
  };

  const handlePrevLetter = () => {
    speechProvider.playChime('tap');
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : ALPHABET_DATA.length - 1));
  };

  const handleNextLetter = () => {
    speechProvider.playChime('tap');
    setSelectedIndex((prev) => (prev < ALPHABET_DATA.length - 1 ? prev + 1 : 0));
  };

  const handleStrokeProgress = (nextIdx: number, total: number) => {
    setCurrentStepIndex(nextIdx);
    if (nextIdx < total) {
      const nextStroke = currentStrokes[nextIdx];
      const msg = `Bagus! Lanjut ke nomor ${nextStroke.label}: ${nextStroke.instruction}!`;
      setMascotMessage(msg);
      setMascotState('encourage');
      speechProvider.speak(msg, { rate: 0.88 });
    }
  };

  const handleStrokeIncomplete = (_strokeIdx: number, stroke: LetterStroke) => {
    const msg = `Hampir! Coba lagi, ikuti garis ${stroke.label}: ${stroke.instruction}!`;
    setMascotMessage(msg);
    setMascotState('encourage');
    speechProvider.speak(msg, { rate: 0.88 });
  };

  const handleLetterCompleted = () => {
    completionLogCountRef.current += 1;
    console.log('[WritingDebug] handleLetterCompleted', {
      count: completionLogCountRef.current,
      letter: currentDisplayLetter,
      time: new Date().toISOString(),
    });
    speechProvider.playChime('celebrate');
    setMascotState('celebrate');
    setShowCelebration(true);

    const letterKey = isUppercase ? selectedItem.letter : selectedItem.lowercase;
    if (!completedLetters.includes(letterKey)) {
      setCompletedLetters((prev) => [...prev, letterKey]);
    }

    // Award stars and save progress
    appRepository.recordLetterLearned(selectedItem.letter);

    confetti({
      particleCount: 50,
      spread: 70,
      origin: { y: 0.55 },
      colors: ['#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#8B5CF6'],
    });

    const celebrationText = `Hore! Kamu hebat sekali berhasil menulis huruf ${currentDisplayLetter}! ${currentDisplayLetter} untuk ${selectedItem.exampleWord}!`;
    console.log('[WritingDebug] celebration speech requested', {
      text: celebrationText,
      time: new Date().toISOString(),
    });
    setMascotMessage(celebrationText);
    speechProvider.speak(celebrationText, { rate: 0.88 });
  };

  const handlePlayLetterAudio = () => {
    speechProvider.playChime('tap');
    speechProvider.speak(
      `Huruf ${currentDisplayLetter}. Bunyinya ${selectedItem.phonics}. Contoh kata: ${selectedItem.exampleWord}.`,
      { rate: 0.85 }
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8] flex flex-col select-none pb-12">
      {/* Top Header Bar */}
      <header className="bg-white border-b-2 border-amber-100 shadow-xs sticky top-0 z-30 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          {/* Back Button */}
          <button
            onClick={() => {
              speechProvider.playChime('tap');
              onBack();
            }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-amber-50 hover:bg-amber-100 active:scale-95 text-amber-950 font-display font-black text-xs md:text-sm border border-amber-200 transition cursor-pointer"
          >
            <BackIcon className="w-6 h-6" />
            <span>Menu Utama</span>
          </button>

          {/* Title with Pencil */}
          <div className="flex items-center gap-2">
            <WritingIcon className="w-9 h-9 md:w-10 md:h-10" />
            <div className="text-center sm:text-left">
              <h1 className="font-display font-black text-base md:text-xl text-[#243047] leading-tight">
                Ayo Menulis Huruf
              </h1>
              <p className="text-[11px] md:text-xs text-gray-500 font-medium hidden sm:block">
                Ikuti urutan nomor dan arah garisnya bersama Rara
              </p>
            </div>
          </div>

          {/* Case Toggle Switch Button */}
          <div className="flex items-center bg-gray-100 p-1 rounded-2xl border border-gray-200">
            <button
              onClick={() => handleSelectCase(true)}
              className={`px-3 py-1.5 rounded-xl font-display font-black text-xs transition cursor-pointer ${
                isUppercase
                  ? 'bg-amber-500 text-white shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              BESAR (A)
            </button>
            <button
              onClick={() => handleSelectCase(false)}
              className={`px-3 py-1.5 rounded-xl font-display font-black text-xs transition cursor-pointer ${
                !isUppercase
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              kecil (a)
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-4">
        {/* Step 1: Case Selector Bar (Prominent Banner) */}
        <div className="bg-white rounded-3xl p-3 md:p-4 border-2 border-amber-200 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlphabetIcon className="w-7 h-7" />
            <span className="text-xs md:text-sm font-display font-black text-[#243047]">
              Pilih Jenis Huruf:
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
            <button
              onClick={() => handleSelectCase(true)}
              className={`px-4 py-2.5 rounded-2xl font-display font-black text-xs md:text-sm flex items-center justify-center gap-2 transition cursor-pointer active:scale-95 ${
                isUppercase
                  ? 'bg-linear-to-r from-amber-400 to-yellow-500 text-amber-950 ring-2 ring-amber-300 shadow-sm'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <span className="text-lg">🔠</span>
              <span>Huruf Besar (A-Z)</span>
            </button>

            <button
              onClick={() => handleSelectCase(false)}
              className={`px-4 py-2.5 rounded-2xl font-display font-black text-xs md:text-sm flex items-center justify-center gap-2 transition cursor-pointer active:scale-95 ${
                !isUppercase
                  ? 'bg-linear-to-r from-blue-500 to-indigo-500 text-white ring-2 ring-blue-300 shadow-sm'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <span className="text-lg">🔡</span>
              <span>huruf kecil (a-z)</span>
            </button>
          </div>
        </div>

        {/* Step 2: Alphabet Selector Carousel */}
        <div className="bg-white rounded-3xl p-3 border-2 border-amber-200 shadow-xs">
          <div className="flex items-center justify-between mb-1.5 px-1">
            <span className="text-[11px] md:text-xs font-bold text-gray-500">
              Pilih Huruf untuk Ditulis:
            </span>
            <span className="text-[11px] font-extrabold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
              {selectedIndex + 1} dari 26
            </span>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 pt-1 scrollbar-none">
            {ALPHABET_DATA.map((item, idx) => {
              const displayChar = isUppercase ? item.letter : item.lowercase;
              const isSelected = idx === selectedIndex;
              const isDone = completedLetters.includes(displayChar);

              return (
                <button
                  key={item.id}
                  onClick={() => handleSelectLetter(idx)}
                  className={`shrink-0 w-12 h-14 md:w-14 md:h-16 rounded-2xl flex flex-col items-center justify-center transition-all active:scale-90 cursor-pointer relative ${
                    isSelected
                      ? isUppercase
                        ? 'bg-amber-400 text-amber-950 font-black shadow-md ring-3 ring-amber-300 scale-105'
                        : 'bg-blue-600 text-white font-black shadow-md ring-3 ring-blue-300 scale-105'
                      : isDone
                      ? 'bg-emerald-50 text-emerald-900 border-2 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-gray-50 hover:bg-amber-50 text-gray-700 border border-gray-200'
                  }`}
                >
                  <span className="text-xl md:text-2xl font-display font-black leading-none">
                    {displayChar}
                  </span>
                  <span className="text-[9px] text-gray-500 font-bold mt-0.5">
                    {item.emoji}
                  </span>
                  {isDone && (
                    <span className="absolute -top-1 -right-1 text-[10px] bg-yellow-400 rounded-full px-0.5">
                      ⭐
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mascot Speech Bubble Guidance */}
        <div className="pt-1">
          <RaraMascot
            state={mascotState}
            message={mascotMessage}
            size="sm"
            onBubbleClick={() => {
              speechProvider.speak(mascotMessage, { rate: 0.88 });
            }}
          />
        </div>

        {/* Tracing Canvas Stage with Prev/Next Controls */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Left Column: Letter Information & Pronunciation Card */}
          <div className="md:col-span-4 flex flex-col gap-3">
            {/* Target Letter Spotlight */}
            <div className="bg-white rounded-3xl p-5 border-3 border-amber-200 shadow-md text-center flex flex-col items-center">
              <span className="text-xs font-black uppercase tracking-wider text-amber-700 mb-1">
                Huruf Target
              </span>

              <div className="w-24 h-24 rounded-3xl bg-linear-to-br from-amber-50 to-orange-50 border-3 border-amber-300 flex items-center justify-center text-6xl font-display font-black text-[#243047] shadow-inner mb-2">
                {currentDisplayLetter}
              </div>

              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{selectedItem.emoji}</span>
                <span className="text-lg font-display font-black text-[#243047]">
                  {selectedItem.exampleWord}
                </span>
              </div>

              <p className="text-xs text-gray-500 font-medium mb-3">
                Bunyi: <span className="font-black text-amber-900">"{selectedItem.phonics}"</span>
              </p>

              {/* Sound Button */}
              <button
                onClick={handlePlayLetterAudio}
                className="w-full py-2 px-3 rounded-2xl bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-950 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition shadow-2xs"
              >
                <SpeakerIcon className="w-5 h-5" />
                <span>Dengarkan Bunyi</span>
              </button>
            </div>

            {/* Stroke Step Milestones List */}
            <div className="bg-white rounded-3xl p-4 border-2 border-amber-200 shadow-xs">
              <span className="text-xs font-black uppercase text-gray-600 block mb-2">
                Panduan Urutan Garis:
              </span>
              <div className="flex flex-col gap-2">
                {currentStrokes.map((stroke, idx) => {
                  const isActive = idx === currentStepIndex;
                  const isFinished = idx < currentStepIndex || showCelebration;

                  return (
                    <div
                      key={stroke.order}
                      className={`flex items-start gap-2.5 p-2 rounded-xl text-xs transition ${
                        isActive
                          ? 'bg-amber-50 border border-amber-300 font-bold text-amber-950'
                          : isFinished
                          ? 'bg-emerald-50 text-emerald-900'
                          : 'bg-gray-50 text-gray-500'
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center font-black text-[11px] ${
                          isFinished
                            ? 'bg-emerald-500 text-white'
                            : isActive
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-300 text-gray-700'
                        }`}
                      >
                        {isFinished ? '✓' : stroke.label}
                      </span>
                      <p className="leading-tight pt-0.5">
                        {stroke.instruction}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Middle/Right Column: Interactive Pixi Tracing Canvas */}
          <div className="md:col-span-8 flex flex-col items-center justify-center">
            {/* Canvas Container with Prev/Next buttons */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 w-full">
              {/* Prev Button */}
              <button
                onClick={handlePrevLetter}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white hover:bg-amber-50 active:scale-90 text-gray-700 border-2 border-amber-200 shadow-sm flex items-center justify-center text-lg font-black transition cursor-pointer shrink-0"
                title="Huruf Sebelumnya"
              >
                ◀
              </button>

              {/* The Pixi Tracing Canvas */}
              <PixiLetterTracingCanvas
                letter={currentDisplayLetter}
                isUppercase={isUppercase}
                strokes={currentStrokes}
                onLetterCompleted={handleLetterCompleted}
                onStrokeCompleted={handleStrokeProgress}
                onStrokeIncomplete={handleStrokeIncomplete}
              />

              {/* Next Button */}
              <button
                onClick={handleNextLetter}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white hover:bg-amber-50 active:scale-90 text-gray-700 border-2 border-amber-200 shadow-sm flex items-center justify-center text-lg font-black transition cursor-pointer shrink-0"
                title="Huruf Berikutnya"
              >
                ▶
              </button>
            </div>
          </div>
        </div>

        {/* Celebration Dialog Modal when complete */}
        <AnimatePresence>
          {showCelebration && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 350, damping: 24 }}
                className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border-4 border-amber-300 flex flex-col items-center text-center relative"
              >
                <div className="w-24 h-24 rounded-3xl bg-linear-to-br from-amber-400 to-yellow-400 text-white flex items-center justify-center text-5xl mb-3 shadow-lg ring-4 ring-amber-200 animate-bounce">
                  ⭐
                </div>

                <span className="bg-emerald-100 text-emerald-900 px-3.5 py-1 rounded-full text-xs font-black mb-1">
                  SELESAI DITULIS!
                </span>

                <h3 className="text-2xl font-display font-black text-[#243047]">
                  Huruf {currentDisplayLetter}
                </h3>
                <p className="text-xs text-gray-500 font-medium mt-1 mb-4">
                  {selectedItem.exampleWord} {selectedItem.emoji} • Kamu mendapat 2 bintang!
                </p>

                <div className="flex flex-col gap-2 w-full">
                  <button
                    onClick={() => {
                      speechProvider.playChime('tap');
                      setShowCelebration(false);
                      handleNextLetter();
                    }}
                    className="w-full py-3.5 rounded-2xl bg-linear-to-r from-[#4F7CF7] to-[#3B65DB] text-white font-display font-black text-sm flex items-center justify-center gap-2 active:scale-95 cursor-pointer shadow-md transition"
                  >
                    <span>Tulis Huruf Berikutnya</span>
                    <span>➡️</span>
                  </button>

                  <button
                    onClick={() => {
                      speechProvider.playChime('tap');
                      setShowCelebration(false);
                    }}
                    className="w-full py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs flex items-center justify-center gap-1.5 active:scale-95 cursor-pointer transition"
                  >
                    <span>Latihan Huruf {currentDisplayLetter} Lagi</span>
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};
